import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Loader2, Images } from 'lucide-react';
import { usePlanEditor } from './PlanEditorContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASE_LABELS = {
  preparation: '1 · Préparation',
  structure: '2 · Structure',
  pieces: '3 · Pièces',
  validation: '4 · Validation',
};

/**
 * PhaseSnapshotButton — V14.70 (choix utilisateur : capture MANUELLE).
 * Capture le Stage Konva courant (via context.stageRef) et archive
 * l'image de fin de phase côté backend (state.phase_snapshots[phase]).
 */
export const PhaseSnapshotButton = ({ projectId, selectedLotId, phase, disabled }) => {
  const { stageRef } = usePlanEditor();
  const [busy, setBusy] = useState(false);

  const capture = useCallback(async () => {
    const stage = stageRef?.current;
    if (!stage) {
      toast.error('Canvas indisponible pour la capture');
      return;
    }
    setBusy(true);
    try {
      const dataUrl = stage.toDataURL({ pixelRatio: 2, mimeType: 'image/png' });
      const url = `${API}/projects/${projectId}/plan-source/phase-snapshot${selectedLotId ? `?lot_id=${selectedLotId}` : ''}`;
      await axios.post(url, { phase, png_b64: dataUrl }, { withCredentials: true });
      toast.success(`Image de phase « ${PHASE_LABELS[phase] || phase} » sauvegardée 📷`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la capture');
    } finally {
      setBusy(false);
    }
  }, [stageRef, projectId, selectedLotId, phase]);

  return (
    <button
      type="button"
      onClick={capture}
      disabled={disabled || busy}
      title={`Sauvegarder l'image de fin de phase ${PHASE_LABELS[phase] || phase}`}
      className="inline-flex items-center gap-1.5 h-7 px-2 rounded text-[11px] font-medium border border-white/15 text-white/70 hover:bg-white/[0.06] disabled:opacity-40 disabled:cursor-not-allowed"
      data-testid={`phase-snapshot-btn-${phase}`}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
      Snapshot
    </button>
  );
};

/**
 * PhaseSnapshotsGallery — vignettes des images de fin de phase (4·Validation).
 */
export const PhaseSnapshotsGallery = ({ projectId, selectedLotId, savedState }) => {
  const snaps = savedState?.phase_snapshots || {};
  const entries = ['preparation', 'structure', 'pieces', 'validation']
    .filter((p) => snaps[p]?.file_id)
    .map((p) => ({ phase: p, ...snaps[p] }));
  if (entries.length === 0) return null;
  return (
    <div className="col-span-12 rounded-lg border p-3"
         style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'var(--c-border)' }}
         data-testid="phase-snapshots-gallery">
      <h4 className="text-[11px] font-semibold flex items-center gap-1.5 text-gray-300 mb-2">
        <Images className="w-3.5 h-3.5 text-[#D9A05B]" />
        Images de sauvegarde par phase
      </h4>
      <div className="flex items-start gap-3 flex-wrap">
        {entries.map((e) => {
          const src = `${API}/projects/${projectId}/plan-source/render?file_id=${e.file_id}${selectedLotId ? `&lot_id=${selectedLotId}` : ''}`;
          return (
            <a key={e.phase} href={src} target="_blank" rel="noreferrer"
               className="block group" data-testid={`phase-snapshot-thumb-${e.phase}`}>
              <div className="w-36 h-24 rounded border border-white/10 overflow-hidden bg-white">
                <img src={src} alt={`Snapshot ${e.phase}`} crossOrigin="use-credentials"
                     className="w-full h-full object-contain group-hover:scale-105 transition-transform" />
              </div>
              <p className="mt-1 text-[10px] text-gray-400">
                {PHASE_LABELS[e.phase]} · {new Date(e.saved_at).toLocaleDateString('fr-FR')}
              </p>
            </a>
          );
        })}
      </div>
    </div>
  );
};
